import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui';
import {
    bulkDeleteRoleMaps,
    bulkResyncRoleMaps,
    bulkSetRoleMapScope,
    createRoleMap,
    deleteRoleMap,
    deleteRoleMapOverride,
    downloadRoleMapCsv,
    restoreRoleMap,
    triggerCatalogSync,
    updateRoleMap,
    type CatalogSyncResult,
} from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';
import type { RoleMapGroup, RoleMapScope } from './types';

/**
 * EAA-1817: thrown in place of the raw `OrgAdminApiError` when a create/
 * re-point write fails because the app's live catalog no longer has the
 * `app_role` key being written (frame 57's red "Không lưu được" card).
 *
 * WHY a custom error instead of reading the server's own field-level message:
 * at the time this was written, `console.ts`'s `request()` only ever read
 * `envelope.data` into `OrgAdminApiError.details` — always `null` on an error
 * response, since the real per-field text portal-api sends lives at the
 * top-level `envelope.details` — so the specific "'X' is not an active role in
 * the Y catalog" string never survived past `error.message` (the generic
 * translated VALIDATION_ERROR description). **EAA-1884 fixed that copy/paste
 * bug** (`request()` now reads `envelope.details`, see `api/console.ts`), so
 * `error.details.app_role` is readable today. This class/detection path is
 * left as-is regardless: it is a working, already-shipped mechanism, and
 * `error.details` values now arrive as `{code, message}` objects (per
 * `emg_shared/api/validation_envelope.normalize_manual_error_details`), not the
 * bare string this comment used to assume — re-deriving the message
 * client-side from the already-known `app_role`/`application` (as below)
 * stays simpler than parsing that shape back out.
 */
export class StaleCatalogError extends Error {
    readonly appRole: string;
    readonly application: string;
    constructor(appRole: string, application: string) {
        super(`'${appRole}' is not an active role in the ${application} catalog.`);
        this.name = 'StaleCatalogError';
        this.appRole = appRole;
        this.application = application;
    }
}

export function isStaleCatalogError(error: unknown): error is StaleCatalogError {
    return error instanceof StaleCatalogError;
}

/**
 * EAA-1817: `error.code === 2000` is portal-api's `VALIDATION_ERROR` code
 * (`emg_shared/api/error_codes.py`). Every call site that wraps this check
 * below always sends a single, non-empty, well-formed `app_role` (chosen from
 * a live-fetched catalog, never free text) — so for THESE calls specifically,
 * the catalog-staleness check is the only validation rule they can ever trip.
 * This is a safe proxy only because of that; it is not a generic "was this a
 * VALIDATION_ERROR" test suitable for reuse elsewhere.
 */
function isCatalogValidationError(error: unknown): boolean {
    return error instanceof OrgAdminApiError && error.code === 2000;
}

export interface RoleMapWriteContext {
    apiBase: string;
    token: string | null;
    actingOrgId: string | null;
    scope: RoleMapScope;
    /** May edit the ecosystem-default slice (`console.rolemap.manage`). */
    canManageDefault: boolean;
    /** May edit the acting org's override slice (`org:{id}.manage`). */
    canManageOrg: boolean;
    /** Called after any write that can remove rows from the current view. */
    clearSelection: () => void;
}

/**
 * EAA-1837: every write `/console/role-map` can perform, in one place — the four
 * new org-scoped bulk endpoints, the pre-existing single-row create/delete, the
 * catalog sync, and the CSV download.
 *
 * Split out of `useRoleMapTable` so that hook stays "which rows are on screen"
 * and this one stays "what happens when the operator acts", mirroring the
 * scan/view split in `accounts/`. Grouped into one `RoleMapWriteContext` object
 * rather than eight positional arguments, per the root CLAUDE.md's "group
 * related props" rule.
 *
 * Two rules run through everything here:
 *
 * 1. **Never one id per group.** Callers hand over a whole `RoleMapGroup` (or a
 *    pre-expanded id list); a multi-role mapping's every `RoleMap` id goes in
 *    the request body. See `RoleMapGroup`'s docstring for the EAA-1828 bug class.
 * 2. **`skipped` is never swallowed.** The bulk endpoints answer with the ids
 *    they refused (not owned by this org); a partial result is reported as a
 *    partial result, never as a plain success.
 */
export function useRoleMapWrites(context: RoleMapWriteContext) {
    const { apiBase, token, actingOrgId, scope, canManageDefault, canManageOrg, clearSelection } =
        context;
    const queryClient = useQueryClient();
    const toast = useToast();
    // EAA-1845: this hook renders no JSX, but every toast message below is
    // still user-facing text — `useTranslation` is a hook and composes fine
    // from inside another hook.
    const { t } = useTranslation();

    const invalidateAfterWrite = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: ['role-maps'] });
        void queryClient.invalidateQueries({ queryKey: ['role-map-sync-status'] });
    }, [queryClient]);

    const reportSkipped = useCallback(
        (skipped: string[], done: number, doneLabel: string) => {
            if (skipped.length === 0) {
                toast.success(t('console.roleMap.writes.reportSuccess', { doneLabel, done }));
                return;
            }
            toast.error(
                t('console.roleMap.writes.reportSkippedError', {
                    doneLabel,
                    done,
                    skippedCount: skipped.length,
                })
            );
        },
        [toast, t]
    );

    const fail = useCallback(
        (fallback: string) => (error: unknown) =>
            toast.error(error instanceof OrgAdminApiError ? error.message : fallback),
        [toast]
    );

    /**
     * EAA-1817: "Hoàn tác" after a delete (frame 57). `POST .../restore` has NO
     * server-side expiry — it will restore a row deleted an hour ago just as
     * happily as one deleted a second ago — so this 10s window is a purely
     * FRONTEND convention (stop showing a stale "Hoàn tác" button forever), not
     * a guarantee the operation stops being reversible after it elapses.
     *
     * `useToast()`'s `ToastApi` (packages/ui `Toast.tsx`) has no action-button
     * slot — `ToastOptions` is only `{description, duration}` and `message` is
     * a plain `string`, so a real "Hoàn tác" button cannot live inside the
     * toast itself. Rather than hack around that (reach past the hook to
     * antd's raw `notification` API, or otherwise bypass the shared contract),
     * the toast still fires for the plain confirmation and this `pending`
     * state drives a SEPARATE, additive banner (rendered by `RoleMapPage.tsx`)
     * that carries the actual working button.
     */
    const [undoPending, setUndoPending] = useState<{ ids: string[]; label: string } | null>(null);
    const undoTimerRef = useRef<number | null>(null);

    const clearUndoTimer = useCallback(() => {
        if (undoTimerRef.current !== null) {
            window.clearTimeout(undoTimerRef.current);
            undoTimerRef.current = null;
        }
    }, []);
    useEffect(() => clearUndoTimer, [clearUndoTimer]);

    const announceUndo = useCallback(
        (ids: string[], label: string) => {
            if (ids.length === 0) return;
            clearUndoTimer();
            setUndoPending({ ids, label });
            undoTimerRef.current = window.setTimeout(() => setUndoPending(null), 10000);
        },
        [clearUndoTimer]
    );

    const restoreMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            for (const id of ids) {
                await restoreRoleMap(apiBase, token as string, id);
            }
            return ids.length;
        },
        onSuccess: (count) => {
            invalidateAfterWrite();
            clearUndoTimer();
            setUndoPending(null);
            toast.success(t('console.roleMap.writes.restoreSuccess', { count }));
        },
        onError: fail(t('console.roleMap.writes.restoreError')),
    });

    const dismissUndo = useCallback(() => {
        clearUndoTimer();
        setUndoPending(null);
    }, [clearUndoTimer]);

    const scopeMutation = useMutation({
        mutationFn: (input: { ids: string[]; organization_id: string | null }) =>
            bulkSetRoleMapScope(apiBase, token as string, actingOrgId as string, input),
        onSuccess: (data) => {
            invalidateAfterWrite();
            clearSelection();
            reportSkipped(
                data.skipped,
                data.updated.length,
                t('console.roleMap.writes.doneLabelScope')
            );
        },
        onError: fail(t('console.roleMap.writes.scopeError')),
    });

    const resyncMutation = useMutation({
        mutationFn: (ids: string[]) =>
            bulkResyncRoleMaps(apiBase, token as string, actingOrgId as string, ids),
        onSuccess: (data) => {
            invalidateAfterWrite();
            // "Đồng bộ lại" recomputes health rather than changing a mapping, so
            // the useful answer is "did anything come back still broken?" — a
            // bare success would hide exactly what the operator ran this for.
            const stillStale = data.results.filter((result) => result.is_stale).length;
            const detail =
                stillStale > 0
                    ? ` ${t('console.roleMap.writes.resyncSuccessSuffix', { count: stillStale })}`
                    : '';
            if (data.skipped.length > 0) {
                toast.error(
                    t('console.roleMap.writes.resyncSkippedError', {
                        count: data.results.length,
                        detail,
                        skippedCount: data.skipped.length,
                    })
                );
                return;
            }
            toast.success(
                t('console.roleMap.writes.resyncSuccess', { count: data.results.length, detail })
            );
        },
        onError: fail(t('console.roleMap.writes.resyncError')),
    });

    const deleteMutation = useMutation({
        mutationFn: (ids: string[]) =>
            bulkDeleteRoleMaps(apiBase, token as string, actingOrgId as string, ids),
        onSuccess: (data) => {
            invalidateAfterWrite();
            clearSelection();
            reportSkipped(
                data.skipped,
                data.updated.length,
                t('console.roleMap.writes.doneLabelDelete')
            );
            // EAA-1817: only the ids the server actually applied are restorable —
            // `skipped` ids were never touched, so offering "Hoàn tác" for them
            // would call `restore` on a row that was never deleted.
            announceUndo(
                data.updated,
                t('console.roleMap.writes.undoLinkLabel', { count: data.updated.length })
            );
        },
        onError: fail(t('console.roleMap.writes.deleteError')),
    });

    /** Default-scope delete. The org-scoped bulk endpoint cannot touch a row with
     *  `organization_id IS NULL` (it filters `organization_id=org_id`), so the
     *  ecosystem-default slice falls back to the pre-existing single-row
     *  `DELETE /role-maps/{id}` — issued once per id in the group, never once
     *  for the group. */
    const defaultDeleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            for (const id of ids) {
                await deleteRoleMap(apiBase, token as string, id);
            }
            return ids;
        },
        onSuccess: (ids) => {
            invalidateAfterWrite();
            clearSelection();
            toast.success(t('console.roleMap.writes.defaultDeleteSuccess', { count: ids.length }));
            announceUndo(ids, t('console.roleMap.writes.undoLinkLabel', { count: ids.length }));
        },
        onError: fail(t('console.roleMap.writes.deleteError')),
    });

    const createMutation = useMutation({
        mutationFn: async (input: {
            role_key: string;
            application: string;
            appRoles: string[];
        }) => {
            // EAA-1837: a multi-role mapping IS N separate RoleMap rows sharing one
            // (role_key, application) pair — created one per app_role here, which is
            // what makes the table's grouped multi-chip row exist at all.
            for (const appRole of input.appRoles) {
                try {
                    await createRoleMap(apiBase, token as string, {
                        role_key: input.role_key,
                        application: input.application,
                        app_role: appRole,
                        ...(scope === 'org' && actingOrgId ? { organization_id: actingOrgId } : {}),
                    });
                } catch (error) {
                    // EAA-1817: the specific "LMS đã bỏ vai trò X" recovery (frame 57)
                    // needs to know exactly which app_role tripped the catalog check —
                    // known here client-side (the value this iteration just sent),
                    // never parsed back out of the server's message (see
                    // `StaleCatalogError`'s docstring for why that path is closed).
                    if (isCatalogValidationError(error)) {
                        throw new StaleCatalogError(appRole, input.application);
                    }
                    throw error;
                }
            }
            return input.appRoles.length;
        },
        onSuccess: (count) => {
            invalidateAfterWrite();
            toast.success(t('console.roleMap.writes.createSuccess', { count }));
        },
        onError: (error) => {
            // EAA-1817: a multi-role submit can create SOME app_roles before the
            // catalog check fails on a later one — invalidate even on failure so
            // the table reflects whatever DID get created, rather than only
            // updating on the next unrelated refetch.
            invalidateAfterWrite();
            // The stale-catalog case gets its own inline recovery card in
            // `RoleMapAddModal` (wired to `createMutation.error`) instead of a
            // generic toast — showing both would be double messaging for one cause.
            if (!isStaleCatalogError(error)) fail(t('console.roleMap.writes.createError'))(error);
        },
    });

    /**
     * EAA-1817: "Đổi vai trò" — re-points ONE `RoleMap` row at a
     * different `app_role` in a single PATCH, replacing the old delete+create
     * two-step (two audit records for one human decision, nothing to undo as a
     * unit). `PATCH /role-maps/{id}` is gated on `ROLEMAP_MANAGE` via
     * `@require_permissions_for_method(patch=…, delete=…)` on `RoleMapDetailView`,
     * not a superadmin check — and that is what `console.rolemap.manage` carries, for
     * an org-scope row as much as a default one, so callers must only expose this
     * action when `canManageDefault` regardless of the scope on screen.
     */
    const repointMutation = useMutation({
        mutationFn: async (input: { id: string; appRole: string; application: string }) => {
            try {
                return await updateRoleMap(apiBase, token as string, input.id, {
                    app_role: input.appRole,
                });
            } catch (error) {
                if (isCatalogValidationError(error)) {
                    throw new StaleCatalogError(input.appRole, input.application);
                }
                throw error;
            }
        },
        onSuccess: () => {
            invalidateAfterWrite();
            toast.success(t('console.roleMap.writes.repointSuccess'));
        },
        onError: (error) => {
            if (!isStaleCatalogError(error)) fail(t('console.roleMap.writes.repointError'))(error);
        },
    });

    /**
     * EAA-1817: "Về mặc định" (frame 59) — drops an org's override rows for one
     * (role_key, application) pair so resolution falls back to the ecosystem
     * default. `DELETE /role-map/override` is `IsPortalAdmin` (super-admin OR
     * that org's own admin), matching `canManageOrg` — unlike the impact/
     * restore/re-point endpoints above, org-admins CAN reach this one.
     */
    const dropOverrideMutation = useMutation({
        mutationFn: (input: { application: string; roleKey: string; organization: string }) =>
            deleteRoleMapOverride(apiBase, token as string, input),
        onSuccess: (result) => {
            invalidateAfterWrite();
            toast.success(
                t('console.roleMap.writes.dropOverrideSuccess', { count: result.dropped })
            );
        },
        onError: fail(t('console.roleMap.writes.dropOverrideError')),
    });

    const syncMutation = useMutation({
        mutationFn: () => triggerCatalogSync(apiBase, token as string),
        onSuccess: (data: { results: CatalogSyncResult[] }) => {
            invalidateAfterWrite();
            void queryClient.invalidateQueries({ queryKey: ['admin-app-roles'] });
            const synced = data.results.filter((r) => r.status === 'synced').length;
            const skipped = data.results.length - synced;
            toast.success(
                skipped > 0
                    ? t('console.roleMap.writes.syncSuccessWithSkipped', { synced, skipped })
                    : t('console.roleMap.writes.syncSuccess', { synced })
            );
        },
        onError: fail(t('console.roleMap.writes.syncError')),
    });

    const [exportBusy, setExportBusy] = useState(false);
    const exportCsv = useCallback(
        async (ids: string[], includeCodes = false) => {
            if (!actingOrgId) return;
            setExportBusy(true);
            try {
                // Selection narrows the export; with nothing selected `ids` is
                // omitted and the server exports every row in the org's scope.
                //
                // EAA-1817: `includeCodes` is frame 55c's "kèm cột mã vai trò".
                await downloadRoleMapCsv(
                    apiBase,
                    token as string,
                    actingOrgId,
                    ids.length > 0 ? ids : undefined,
                    includeCodes
                );
            } catch (error) {
                fail(t('console.roleMap.writes.exportError'))(error);
            } finally {
                setExportBusy(false);
            }
        },
        [actingOrgId, apiBase, token, fail, t]
    );

    /** Row "⋯" → Xoá. Routes through whichever endpoint can actually reach the
     *  row's scope, and always with the group's FULL id list. */
    const removeGroup = useCallback(
        (group: RoleMapGroup) => {
            if (scope === 'org' && actingOrgId && canManageOrg) {
                deleteMutation.mutate(group.ids);
                return;
            }
            if (canManageDefault) {
                defaultDeleteMutation.mutate(group.ids);
                return;
            }
            toast.error(t('console.roleMap.writes.removeGroupPermissionError'));
        },
        [
            scope,
            actingOrgId,
            canManageOrg,
            canManageDefault,
            deleteMutation,
            defaultDeleteMutation,
            toast,
            t,
        ]
    );

    const busy =
        scopeMutation.isPending ||
        resyncMutation.isPending ||
        deleteMutation.isPending ||
        defaultDeleteMutation.isPending;

    return {
        busy,
        exportBusy,
        exportCsv,
        removeGroup,
        setScopeFor: scopeMutation.mutate,
        resyncIds: resyncMutation.mutate,
        deleteIds: deleteMutation.mutate,
        createLinks: createMutation.mutateAsync,
        createBusy: createMutation.isPending,
        // EAA-1817: exposed wholesale (not just `.mutate`/`isPending`, unlike the
        // bulk mutations above) because `RoleMapAddModal` needs to read
        // `.error`/`.reset` too, to drive the stale-catalog recovery card.
        createMutation,
        repointMutation,
        dropOverrideMutation,
        syncMutation,
        undo: {
            pending: undoPending,
            restore: () => undoPending && restoreMutation.mutate(undoPending.ids),
            dismiss: dismissUndo,
            busy: restoreMutation.isPending,
        },
    };
}
